import React, { ReactElement, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { GeodataProcessType } from '../../../../../../../typings/api/skymap/rest/v1/.common';
import OrthoOnlyIcon from '../../../../../../static/assets/img/cloudprocessing/orthophoto-only.svg?react';
import PointcloudOnlyIcon from '../../../../../../static/assets/img/cloudprocessing/pointcloud-only.svg?react';
import StandardIcon from '../../../../../../static/assets/img/cloudprocessing/standard.svg?react';
import { FeatureFlagsManager } from '../../../../../utils/core/feature-flags-manager';
import { ThemeManager } from '../../../../themes/theme-manager';
import { Button } from '../../../button/button';
import { CheckBox } from '../../../check-box/check-box';
import { Hyperlink } from '../../../hyperlink/hyperlink';
import { IconPanel } from '../../../icon-panel/icon-panel';
import { Stack } from '../../../stack/stack';

type Props = {
  onSuccess: (processType: GeodataProcessType) => void;
};

type CardProps = {
  disabled: boolean;
  onClick: (processType: GeodataProcessType) => void;
};

export const SelectProcessType = (props: Props) => {
  const [termsOfServiceChecked, setTermsOfServiceChecked] = useState(false);
  return (
    <Component spacing={1}>
      <ProcessingCardsContainer direction="row" spacing={1.2}>
        <Standard disabled={!termsOfServiceChecked} onClick={() => props.onSuccess('FULL')} />
        <PointcloudOnly
          disabled={!termsOfServiceChecked}
          onClick={() => props.onSuccess('POINTCLOUD_ONLY')}
        />
        <OrthoOnly
          disabled={!termsOfServiceChecked}
          onClick={() => props.onSuccess('ORTHO_ONLY')}
        />
        <Premium
          disabled={!termsOfServiceChecked}
          onClick={() => props.onSuccess('FULL_HIGH_QUALITY')}
        />
        {FeatureFlagsManager.instance.isEnabled('SK-6613-signature-processing-package') && (
          <Signature
            disabled={!termsOfServiceChecked}
            onClick={() => props.onSuccess('SIGNATURE')}
          />
        )}
        {FeatureFlagsManager.instance.isEnabled('SK-5443-beta-pointcloud-pipeline') && (
          <Odm disabled={!termsOfServiceChecked} onClick={() => props.onSuccess('ODM')} />
        )}
        {FeatureFlagsManager.instance.isEnabled('SK-6920-pm') && (
          <Pm disabled={!termsOfServiceChecked} onClick={() => props.onSuccess('PM')} />
        )}
      </ProcessingCardsContainer>

      <CheckBox
        checked={termsOfServiceChecked}
        disabled={false}
        onChange={() => setTermsOfServiceChecked(!termsOfServiceChecked)}
      >
        <Trans
          components={{
            hyperlink: (
              <Hyperlink
                target="_blank"
                url={ThemeManager.instance.getCloudProcessingTermsOfServiceUrl()}
              />
            ),
          }}
          i18nKey="setupDialog.readAndAcceptTermsOfServiceCloudProcessing"
          ns="cloudProcessing"
        />
      </CheckBox>
    </Component>
  );
};

const Standard = (props: CardProps) => {
  const { t } = useTranslation();
  return (
    <Card
      disabled={props.disabled}
      info={t('setupDialog.cards.standard.text', { ns: 'cloudProcessing' })}
      primary={true}
      title={t('setupDialog.cards.standard.title', { ns: 'cloudProcessing' })}
      onClick={() => props.onClick('FULL')}
    />
  );
};

const Premium = (props: CardProps) => {
  const { t } = useTranslation();
  return (
    <Card
      disabled={props.disabled}
      info={t('setupDialog.cards.premium.text', { ns: 'cloudProcessing' })}
      title={t('setupDialog.cards.premium.title', { ns: 'cloudProcessing' })}
      onClick={() => props.onClick('FULL_HIGH_QUALITY')}
    />
  );
};

const Signature = (props: CardProps) => {
  const { t } = useTranslation();
  return (
    <Card
      disabled={props.disabled}
      info={t('setupDialog.cards.signature.text', { ns: 'cloudProcessing' })}
      title={t('setupDialog.cards.signature.title', { ns: 'cloudProcessing' })}
      onClick={() => props.onClick('SIGNATURE')}
    />
  );
};

const Odm = (props: CardProps) => {
  const { t } = useTranslation();
  if (!FeatureFlagsManager.instance.isEnabled('SK-5443-beta-pointcloud-pipeline')) {
    return null;
  }

  return (
    <Card
      disabled={props.disabled}
      info={t('setupDialog.cards.odm.text', { ns: 'cloudProcessing' })}
      title={t('setupDialog.cards.odm.title', { ns: 'cloudProcessing' })}
      onClick={() => props.onClick('ODM')}
    />
  );
};

const Pm = (props: CardProps) => {
  const { t } = useTranslation();
  return (
    <Card
      disabled={props.disabled}
      info={t('setupDialog.cards.pm.text', { ns: 'cloudProcessing' })}
      title={t('setupDialog.cards.pm.title', { ns: 'cloudProcessing' })}
      onClick={() => props.onClick('PM')}
    />
  );
};

const PointcloudOnly = (props: CardProps) => {
  const { t } = useTranslation();
  return (
    <Card
      disabled={props.disabled}
      icon={<PointcloudOnlyIcon />}
      info={t('setupDialog.cards.pointCloud.text', { ns: 'cloudProcessing' })}
      title={t('setupDialog.cards.pointCloud.title', { ns: 'cloudProcessing' })}
      onClick={() => props.onClick('POINTCLOUD_ONLY')}
    />
  );
};

const OrthoOnly = (props: CardProps) => {
  const { t } = useTranslation();
  return (
    <Card
      disabled={props.disabled}
      icon={<OrthoOnlyIcon />}
      info={t('setupDialog.cards.orthophoto.text', { ns: 'cloudProcessing' })}
      title={t('setupDialog.cards.orthophoto.title', { ns: 'cloudProcessing' })}
      onClick={() => props.onClick('ORTHO_ONLY')}
    />
  );
};

const Card = (props: {
  icon?: ReactElement;
  title: string;
  disabled?: boolean;
  info: string;
  primary?: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <ProcessingCard spacing={0.5} title={props.title}>
      <IconPanel
        alignContent="start"
        header={props.icon ?? <StandardIcon />}
        responsiveness={{ compressed: false }}
      >
        {{
          title: props.title,
          info: <Flex>{props.info}</Flex>,
        }}
      </IconPanel>
      <Button
        color={props.primary ? 'primary' : 'secondary'}
        disabled={props.disabled}
        variant="contained"
        onClick={props.onClick}
      >
        {t('select', { ns: 'common' })}
      </Button>
    </ProcessingCard>
  );
};

const Component = styled(Stack)`
  user-select: none;
`;

const ProcessingCardsContainer = styled(Stack)`
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0.2em 1.2em;
`;

const Flex = styled.div`
  flex: 1;
`;

const ProcessingCard = styled(Stack)`
  padding: 1em;
  width: calc(140px + 2em); // Width needs to account for SVG element width and padding.

  &:hover {
    background-color: ${({ theme }) => theme.color.gray.lightest};
  }
`;
